import { Recipe } from '../types';
import { BreedSize, RecipeRecommendationRulesConfig } from '../types/config';

export interface DogProfile {
  ageCategory: string;
  activityLevel: string;
  bodyCondition: string;
  pathologies: string[];
  breedSize: BreedSize;
  dogName: string;
}

export interface ScoredRecipe {
  recipe: Recipe;
  score: number;
  isRecommended: boolean;
  reasons: string[];
  recommendationText: string;
}

export interface RecipeRecommendationOptions {
  /**
   * Dog age category used to pick the target life-stage pool. When provided,
   * the recipe pool is filtered by `lifeStage` before scoring.
   * - 'cachorro' -> recipes with lifeStage 'puppy' (or 'all' / unset)
   * - 'adulto' | 'senior' -> recipes with lifeStage 'adult' (or 'all' / unset)
   */
  dogAgeCategory?: 'cachorro' | 'adulto' | 'senior';
}

/**
 * Score recipes based on dog profile and recommendation config.
 * Returns all recipes sorted by score (highest first) with recommendation flags.
 *
 * When `options.dogAgeCategory` is provided, the recipe pool is first filtered
 * by `lifeStage` so puppies only see puppy recipes and adults/seniors only see
 * adult recipes. Recipes without a `lifeStage` value or with `lifeStage === 'all'`
 * are always kept.
 */
export function scoreRecipes(
  recipes: Recipe[],
  dogProfile: DogProfile,
  config: RecipeRecommendationRulesConfig,
  options: RecipeRecommendationOptions = {},
): ScoredRecipe[] {
  // Filter by life stage when a known age is provided.
  let pool = recipes;
  if (options.dogAgeCategory) {
    const targetLifeStage: 'puppy' | 'adult' = options.dogAgeCategory === 'cachorro' ? 'puppy' : 'adult';
    pool = recipes.filter((r) => !r.lifeStage || r.lifeStage === targetLifeStage || r.lifeStage === 'all');
  }

  if (!config.enabled) {
    return pool.map((recipe) => ({
      recipe,
      score: 0,
      isRecommended: false,
      reasons: [],
      recommendationText: '',
    }));
  }

  const weights = config.scoring_weights;
  const profiles = config.recipe_profiles || {};

  const scored: ScoredRecipe[] = pool.map((recipe) => {
    const profile = profiles[recipe.id];
    if (!profile) {
      return {
        recipe,
        score: 0,
        isRecommended: false,
        reasons: [],
        recommendationText: '',
      };
    }

    let score = (profile.base_priority || 0) * (weights.base_priority || 1);
    const reasons: string[] = [];

    // Pathology matches
    const matchedPathologies = (dogProfile.pathologies || []).filter(
      (p) => p !== 'ninguna' && (profile.good_for_pathologies || []).includes(p),
    );
    if (matchedPathologies.length > 0) {
      score += matchedPathologies.length * (weights.pathology_match || 0);
      reasons.push(...(profile.tags_display || []).slice(0, 1));
    }

    // Age match
    if ((profile.good_for_ages || []).includes(dogProfile.ageCategory)) {
      score += weights.age_match || 0;
      if (reasons.length === 0) {
        reasons.push(`Adecuada para perros ${dogProfile.ageCategory}s`);
      }
    }

    // Body condition match
    if ((profile.good_for_body_conditions || []).includes(dogProfile.bodyCondition)) {
      score += weights.body_condition_match || 0;
    }

    // Activity level match
    if ((profile.good_for_activity_levels || []).includes(dogProfile.activityLevel)) {
      score += weights.activity_match || 0;
    }

    // Breed size match
    if ((profile.good_for_breed_sizes || []).includes(dogProfile.breedSize)) {
      score += weights.breed_size_match || 0;
    }

    const recommendationText = (profile.recommendation_text || '').replace(
      /\{dogName\}/g,
      dogProfile.dogName || 'tu perro',
    );

    return {
      recipe,
      score,
      isRecommended: false,
      reasons,
      recommendationText,
    };
  });

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Mark top N as recommended
  const maxPreselected = config.max_preselected || 3;
  scored.forEach((item, index) => {
    if (index < maxPreselected && item.score > 0) {
      item.isRecommended = true;
    }
  });

  return scored;
}
